import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchGymDetails, updateGym, clearSelectedGym } from '../../redux/gymSlice';
import { RootState, AppDispatch } from '../../redux/store';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { TimePicker } from '../../components/TimePicker';
import { LocationPicker } from '../../components/LocationPicker';

const AMENITIES_OPTIONS = [
  'WiFi',
  'Parking',
  'Locker',
  'Shower',
  'AC',
  'Trainer',
  'Steam',
  'Sauna',
  'Cardio Equipment',
  'Weight Training',
  'Group Classes',
  'Swimming Pool',
  'Spa',
  'Cafe'
];

export const EditGymPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { gymId } = useParams<{ gymId: string }>();
  const { selectedGym, isLoading, error } = useSelector((state: RootState) => state.gym);
  const [successMessage, setSuccessMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    latitude: 0,
    longitude: 0,
    opening_time: '06:00:00',
    closing_time: '22:00:00',
    total_capacity: '',
    amenities: [] as string[]
  });

  useEffect(() => {
    if (gymId) {
      dispatch(fetchGymDetails(parseInt(gymId)));
    }

    return () => {
      dispatch(clearSelectedGym());
    };
  }, [dispatch, gymId]);

  useEffect(() => {
    if (selectedGym) {
      const amenities = Array.isArray(selectedGym.amenities)
        ? selectedGym.amenities
        : (typeof selectedGym.amenities === 'string' ? JSON.parse(selectedGym.amenities) : []);

      setFormData({
        name: selectedGym.name || '',
        description: selectedGym.description || '',
        address: selectedGym.address || '',
        city: selectedGym.city || '',
        state: selectedGym.state || '',
        pincode: selectedGym.pincode || '',
        latitude: selectedGym.latitude || 0,
        longitude: selectedGym.longitude || 0,
        opening_time: selectedGym.opening_time || '06:00:00',
        closing_time: selectedGym.closing_time || '22:00:00',
        total_capacity: selectedGym.total_capacity?.toString() || '',
        amenities: amenities
      });
    }
  }, [selectedGym]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: '' });
    }
  };

  const handleAmenitiesChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFormData({ ...formData, amenities: typeof value === 'string' ? value.split(',') : value });
  };

  const handleLocationSelect = (location: { lat: number; lng: number; address: string }) => {
    setFormData({
      ...formData,
      latitude: location.lat,
      longitude: location.lng,
      address: location.address
    });
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name || formData.name.trim().length < 3) {
      errors.name = 'Gym name must be at least 3 characters';
    }

    const capacity = parseInt(formData.total_capacity);
    if (!formData.total_capacity || isNaN(capacity) || capacity < 1) {
      errors.total_capacity = 'Capacity must be at least 1';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !gymId) {
      return;
    }

    const updateData = {
      name: formData.name,
      description: formData.description,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      pincode: formData.pincode,
      latitude: formData.latitude,
      longitude: formData.longitude,
      opening_time: formData.opening_time,
      closing_time: formData.closing_time,
      total_capacity: parseInt(formData.total_capacity),
      amenities: formData.amenities
    };

    const result = await dispatch(updateGym({ gymId: parseInt(gymId), data: updateData }));

    if (result.type === updateGym.fulfilled.type) {
      setSuccessMessage('Gym updated successfully!');
      setTimeout(() => {
        navigate(`/gyms/${gymId}`);
      }, 1500);
    }
  };

  if (isLoading && !selectedGym) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress sx={{ color: '#14B8A6' }} />
      </Box>
    );
  }

  if (error && !selectedGym) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/gyms')}
          sx={{ mt: 2 }}
        >
          Back to Gyms
        </Button>
      </Container>
    );
  }

  if (!selectedGym) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/gyms/${gymId}`)}
          sx={{ mb: 2 }}
        >
          Back to Gym Details
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          Edit Gym
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Update gym information and settings
        </Typography>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, pb: 1, borderBottom: '2px solid #14B8A6' }}>
              Basic Information
            </Typography>

              <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Gym Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    error={!!fieldErrors.name}
                    helperText={fieldErrors.name}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder="Describe your gym facilities and features"
                  />
                </Grid>
              </Grid>
          </Grid>

          {/* Location Details */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, pb: 1, borderBottom: '2px solid #14B8A6' }}>
              Location Details
            </Typography>

            <Box sx={{ mb: 3 }}>
              <LocationPicker
                onLocationSelect={handleLocationSelect}
                apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
                initialLocation={
                  formData.latitude && formData.longitude
                    ? { lat: Number(formData.latitude), lng: Number(formData.longitude) }
                    : undefined
                }
              />
            </Box>

            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Street address"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Pincode"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  required
                  placeholder="6-digit pincode"
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Operating Hours & Capacity */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, pb: 1, borderBottom: '2px solid #14B8A6' }}>
              Operating Hours & Capacity
            </Typography>

              <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TimePicker
                    label="Opening Time"
                    value={formData.opening_time}
                    onChange={(value) => setFormData({ ...formData, opening_time: value })}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TimePicker
                    label="Closing Time"
                    value={formData.closing_time}
                    onChange={(value) => setFormData({ ...formData, closing_time: value })}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Total Capacity"
                    name="total_capacity"
                    type="number"
                    value={formData.total_capacity}
                    onChange={handleChange}
                    required
                    error={!!fieldErrors.total_capacity}
                    helperText={fieldErrors.total_capacity}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
              </Grid>
          </Grid>

          {/* Amenities */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, pb: 1, borderBottom: '2px solid #14B8A6' }}>
              Amenities
            </Typography>

              <FormControl fullWidth>
                <InputLabel>Select Amenities</InputLabel>
                <Select
                  multiple
                  value={formData.amenities}
                  onChange={handleAmenitiesChange}
                  input={<OutlinedInput label="Select Amenities" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {AMENITIES_OPTIONS.map((amenity) => (
                    <MenuItem key={amenity} value={amenity}>
                      {amenity}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexDirection: { xs: 'column', sm: 'row' } }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/gyms/${gymId}`)}
                sx={{ height: '48px', minWidth: { xs: '100%', sm: '140px' }, order: { xs: 2, sm: 1 } }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <SaveIcon />}
                sx={{
                  height: '48px',
                  minWidth: { xs: '100%', sm: '160px' },
                  backgroundColor: '#14B8A6',
                  '&:hover': {
                    backgroundColor: '#0F9688'
                  },
                  order: { xs: 1, sm: 2 }
                }}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default EditGymPage;
